import React, { useState, useEffect, useCallback } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useParams,
  Link,
  useLocation,
} from "react-router-dom";
import { LogIn, LogOut, Loader, ArrowLeft } from "lucide-react";
import { jsPDF } from "jspdf";
import * as api from "./api/ligaApi";
import LoadingAnimation from "./components/LoadingAnimation";
import LoginPage from "./views/LoginPage";
import Dashboard from "./views/Dashboard";
import AdminPanel from "./views/AdminPanel";
import DesafiosListPage from "./views/DesafiosListPage";
import { useNotification } from "./context/NotificationContext";

// --- Componente Principal ---
export default function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      setIsAdmin(true);
    }
  }, []);

  const handleLogin = async (email, password) => {
    try {
      const data = await api.loginAdmin(email, password);
      if (data.success && data.token) {
        localStorage.setItem("adminToken", data.token);
        setIsAdmin(true);
        navigate("/");
      } else {
        showNotification({
          title: "Falha no Login",
          message: data.message || "Credenciais inválidas.",
        });
      }
    } catch (err) {
      showNotification({ title: "Erro de Login", message: err.message });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setIsAdmin(false);
    navigate("/");
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen font-sans">
      <Header isAdmin={isAdmin} onLogout={handleLogout} />
      <main className="p-4 md:p-8">
        <Routes>
          <Route path="/" element={<DesafiosListPage isAdmin={isAdmin} />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route
            path="/desafio/:desafioId/*"
            element={<DesafioWrapper isAdmin={isAdmin} />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </div>
  );
}

// --- Componentes de Layout e de Carregamento de Dados ---

function Header({ isAdmin, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const inDesafioPage = location.pathname.startsWith("/desafio/");
  const pathParts = location.pathname.split("/");
  const desafioId = inDesafioPage ? pathParts[2] : null;

  return (
    <header className="bg-gray-800/50 backdrop-blur-sm shadow-lg p-4 flex justify-between items-center">
      <div className="flex items-center">
        {inDesafioPage && (
          <button
            onClick={() => navigate("/")}
            className="mr-4 p-2 rounded-full hover:bg-gray-700"
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <Link to="/" className="flex items-center">
          <img src="/logo-desafio.png" alt="Logo" className="w-10 h-10 mr-3" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
            Desafio de Duplas
          </h1>
        </Link>
      </div>
      <nav className="flex items-center space-x-4">
        {isAdmin && inDesafioPage && desafioId && (
          <Link
            to={
              location.pathname.includes("/admin")
                ? `/desafio/${desafioId}`
                : `/desafio/${desafioId}/admin`
            }
            className="text-gray-300 hover:text-white"
          >
            {location.pathname.includes("/admin") ? "Dashboard" : "Admin"}
          </Link>
        )}
        {isAdmin ? (
          <button
            onClick={onLogout}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 text-sm rounded-lg flex items-center"
          >
            <LogOut size={16} className="mr-2" /> Sair
          </button>
        ) : (
          <Link
            to="/login"
            className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-3 text-sm rounded-lg flex items-center"
          >
            <LogIn size={16} className="mr-2" /> Admin Login
          </Link>
        )}
      </nav>
    </header>
  );
}

function DesafioWrapper({ isAdmin }) {
  const { desafioId } = useParams();
  const [desafioData, setDesafioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const fetchDesafioData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getDadosCompletosDesafio(desafioId);
      setDesafioData(data);
    } catch (error) {
      showNotification({
        title: "Erro",
        message: "Erro ao carregar dados do desafio.",
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [desafioId, navigate, showNotification]);

  useEffect(() => {
    fetchDesafioData();
  }, [fetchDesafioData]);

  const generatePdf = async (data, subTitle, filename) => {
    setIsGeneratingPdf(true);
    try {
      const CARD_MARGIN = 15;
      const CARD_SPACING = 7;
      const HEADER_HEIGHT = 16;
      const ROW_HEIGHT = 14;
      const FOOTER_HEIGHT = 16;
      const PAGE_WIDTH = 210; // largura fixa

      const loadImage = (url) => {
        return new Promise((resolve) => {
          if (!url) return resolve(null);
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => {
            resolve({
              src: img.src,
              width: img.width,
              height: img.height,
            });
          };
          img.onerror = () => resolve(null);
          img.src = url;
        });
      };

      // Carrega imagens
      const allTimes = data.flatMap((dupla) => dupla.times || []);
      const escudoPromises = allTimes.map((time) =>
        loadImage(api.getEscudoUrl(time.escudo_url))
      );
      const logoPromises = data.map((dupla) =>
        loadImage(api.getEscudoUrl(dupla.logo_url))
      );

      const loadedEscudos = await Promise.all(escudoPromises);
      const loadedLogos = await Promise.all(logoPromises);

      const escudoMap = allTimes.reduce((map, time, index) => {
        if (loadedEscudos[index]) map[time.id] = loadedEscudos[index];
        return map;
      }, {});
      const logoMap = data.reduce((map, dupla, index) => {
        if (loadedLogos[index]) map[dupla.id] = loadedLogos[index];
        return map;
      }, {});

      // 🔹 Calcula altura necessária
      const totalHeightNeeded = data.reduce((acc, dupla) => {
        const cardHeight =
          HEADER_HEIGHT +
          (dupla.times?.length || 0) * ROW_HEIGHT +
          FOOTER_HEIGHT;
        return acc + cardHeight + CARD_SPACING;
      }, 42); // margem inicial

      // 🔹 Cria PDF com altura dinâmica
      const doc = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: [PAGE_WIDTH, totalHeightNeeded],
      });

      // Fundo
      doc.setFillColor("#282828");
      doc.rect(0, 0, PAGE_WIDTH, totalHeightNeeded, "F");

      // Título
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor("#FFFFFF");
      doc.text("DESAFIO DE DUPLAS", PAGE_WIDTH / 2, 22, { align: "center" });

      // Subtítulo
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor("#A1A1AA");
      doc.text(subTitle, PAGE_WIDTH / 2, 30, { align: "center" });

      let yPos = 42;

      // Loop das duplas
      for (const [index, dupla] of data.entries()) {
        const cardWidth = PAGE_WIDTH - CARD_MARGIN * 2;
        const cardHeight =
          HEADER_HEIGHT +
          (dupla.times || []).length * ROW_HEIGHT +
          FOOTER_HEIGHT;
        const cardX = (PAGE_WIDTH - cardWidth) / 2;

        // Card base
        doc.setFillColor("#18181B");
        doc.roundedRect(cardX, yPos, cardWidth, cardHeight, 3, 3, "F");

        // Cabeçalho do card
        const headerYCenter = yPos + HEADER_HEIGHT / 2;
        doc.setFillColor("#1F2937");
        doc.roundedRect(cardX, yPos, cardWidth, HEADER_HEIGHT, 3, 3, "F");

        // Logo da dupla
        const logo = logoMap[dupla.id];
        if (logo) {
          const maxDim = 10;
          const ratio = Math.min(maxDim / logo.width, maxDim / logo.height);
          const imgWidth = logo.width * ratio;
          const imgHeight = logo.height * ratio;
          const yOffset = headerYCenter - imgHeight / 2;
          doc.addImage(
            logo.src,
            "PNG",
            cardX + 4,
            yOffset,
            imgWidth,
            imgHeight,
            undefined,
            "FAST"
          );
        }

        // Nome e posição
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor("#A1A1AA");
        doc.text(`${index + 1}º`, cardX + (logo ? 16 : 5), headerYCenter, {
          baseline: "middle",
        });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor("#FFFFFF");
        doc.text(dupla.nome, cardX + (logo ? 29 : 18), headerYCenter, {
          baseline: "middle",
        });

        // Times
        let rowY = yPos + HEADER_HEIGHT;
        const timesOrdenados = (dupla.times || []).sort(
          (a, b) =>
            b.pontuacao - a.pontuacao ||
            (a.nome_time || "").localeCompare(b.nome_time || "")
        );

        for (const time of timesOrdenados) {
          doc.setDrawColor("#3F3F46");
          doc.setLineWidth(0.2);
          doc.line(cardX + 2, rowY, cardX + cardWidth - 2, rowY);
          rowY += ROW_HEIGHT;

          // Escudo do time
          const escudo = escudoMap[time.id];
          if (escudo) {
            const maxDim = 10;
            const ratio = Math.min(
              maxDim / escudo.width,
              maxDim / escudo.height
            );
            const imgWidth = escudo.width * ratio;
            const imgHeight = escudo.height * ratio;
            doc.addImage(
              escudo.src,
              "PNG",
              cardX + 4,
              rowY - ROW_HEIGHT / 2 - imgHeight / 2,
              imgWidth,
              imgHeight,
              undefined,
              "FAST"
            );
          }

          // Nome do time e capitão
          const textX = cardX + 17;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor("#FFFFFF");
          doc.text(time.nome_time, textX, rowY - 8);
          if (time.is_captain) {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor("#FBBF24");
            const timeNameWidth = doc.getTextWidth(time.nome_time);
            doc.text("(C)", textX + timeNameWidth + 2, rowY - 8);
          }

          // Nome do cartoleiro
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor("#A1A1AA");
          doc.text(time.nome_cartoleiro, textX, rowY - 3.5);

          // Pontuação
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.setTextColor("#FFFFFF");
          doc.text(
            time.pontuacao.toFixed(2),
            PAGE_WIDTH - CARD_MARGIN - 4,
            rowY - 7,
            { align: "right" }
          );
        }

        // Rodapé do card
        const footerY = yPos + cardHeight - FOOTER_HEIGHT;
        doc.setFillColor("#1F2937");
        doc.roundedRect(cardX, footerY, cardWidth, FOOTER_HEIGHT, 3, 3, "F");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor("#A1A1AA");
        doc.text("PONTUAÇÃO TOTAL DA DUPLA", PAGE_WIDTH / 2, footerY + 7, {
          align: "center",
        });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor("#FFFFFF");
        doc.text(dupla.pontuacao.toFixed(2), PAGE_WIDTH / 2, footerY + 13.5, {
          align: "center",
        });

        yPos += cardHeight + CARD_SPACING;
      }

      // Data de geração
      doc.setFontSize(8);
      doc.setTextColor("#A1A1AA");
      doc.text(
        `Gerado em: ${new Date().toLocaleDateString("pt-BR")}`,
        PAGE_WIDTH - CARD_MARGIN,
        totalHeightNeeded - 10,
        { align: "right" }
      );

      doc.save(`${filename}.pdf`);
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
      showNotification({
        title: "Erro",
        message: "Não foi possível gerar o relatório.",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleGerarPDFGeral = () => {
    const { duplas, rodadas, desafio } = desafioData;
    const calcularPontuacaoTotalTime = (timeId) =>
      (rodadas || [])
        .filter((p) => p.time_id?.toString() === timeId.toString())
        .reduce((acc, curr) => acc + (curr.pontos || 0), 0);
    const dadosParaPdf = (duplas || [])
      .map((dupla) => ({
        ...dupla,
        times: (dupla.times || []).map((time) => ({
          ...time,
          pontuacao: calcularPontuacaoTotalTime(time.id),
        })),
        pontuacao: (dupla.times || []).reduce(
          (acc, time) => acc + calcularPontuacaoTotalTime(time.id),
          0
        ),
      }))
      .sort((a, b) => b.pontuacao - a.pontuacao);
    generatePdf(
      dadosParaPdf,
      `Relatório Geral - ${desafio.nome}`,
      "Relatorio_Geral_Acumulado"
    );
  };

  const handleGerarPDF = (rodadaNum) => {
    const { duplas, rodadas, desafio } = desafioData;
    const getPontosPorTimeNaRodada = (timeId) =>
      (rodadas || []).find(
        (r) =>
          r.numero === rodadaNum && r.time_id?.toString() === timeId.toString()
      )?.pontos || 0;
    const dadosParaPdf = (duplas || [])
      .map((dupla) => ({
        ...dupla,
        times: (dupla.times || []).map((time) => ({
          ...time,
          pontuacao: getPontosPorTimeNaRodada(time.id),
        })),
        pontuacao: (dupla.times || []).reduce(
          (acc, time) => acc + getPontosPorTimeNaRodada(time.id),
          0
        ),
      }))
      .sort((a, b) => b.pontuacao - a.pontuacao);
    generatePdf(
      dadosParaPdf,
      `Pontuação da Rodada ${rodadaNum} - ${desafio.nome}`,
      `Relatorio_Rodada_${rodadaNum}`
    );
  };

  if (loading || !desafioData)
    return (
      <div className="text-center p-10">
        <LoadingAnimation size={200} />
      </div>
    );

  return (
    <>
      {isGeneratingPdf && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex flex-col justify-center items-center">
          <Loader className="animate-spin text-teal-400 h-12 w-12 mb-4" />
          <p className="text-lg text-white">A gerar o seu relatório...</p>
        </div>
      )}
      <Routes>
        <Route
          path="/"
          element={
            <Dashboard
              ligaData={desafioData}
              isAdmin={isAdmin}
              handleGerarPDF={handleGerarPDF}
              handleGerarPDFGeral={handleGerarPDFGeral}
            />
          }
        />
        {isAdmin && <Route path="/admin" element={<AdminWrapper />} />}
      </Routes>
    </>
  );
}

function AdminWrapper() {
  const { desafioId } = useParams();
  const [desafioData, setDesafioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getDadosCompletosDesafio(desafioId);
      setDesafioData(data);
    } catch (error) {
      showNotification({
        title: "Erro",
        message: "Erro ao carregar dados de administração.",
      });
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [desafioId, navigate, showNotification]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const handleAction = async (action, successMessage) => {
    try {
      const result = await action();
      const message = successMessage || (result && result.message);
      if (message) {
        showNotification({ title: "Sucesso", message });
      }
      fetchAdminData();
    } catch (error) {
      showNotification({ title: "Erro", message: error.message });
    }
  };

  if (loading || !desafioData)
    return (
      <div className="text-center p-10">
        <LoadingAnimation size={200} />
      </div>
    );

  return (
    <AdminPanel
      desafioId={desafioId}
      duplas={desafioData.duplas || []}
      onAddDupla={(nome) => handleAction(() => api.addDupla(nome, desafioId))}
      onRemoveDupla={(id) =>
        showNotification({
          title: "Confirmar Exclusão",
          message:
            "Tem a certeza que quer apagar a dupla e todos os seus times?",
          type: "confirm",
          onConfirm: () =>
            handleAction(
              () => api.removeDupla(id),
              "Dupla removida com sucesso."
            ),
        })
      }
      onAddTime={(duplaId, timeData) =>
        handleAction(() =>
          api.addTime({ ...timeData, dupla_id: duplaId, desafio_id: desafioId })
        )
      }
      onRemoveTime={(id) =>
        showNotification({
          title: "Confirmar Exclusão",
          message: "Tem a certeza que quer apagar este time?",
          type: "confirm",
          onConfirm: () =>
            handleAction(
              () => api.removeTime(id),
              "Time removido com sucesso."
            ),
        })
      }
      onSetCaptain={(timeId, duplaId) =>
        handleAction(() => api.setCaptain(timeId, duplaId))
      }
      onUploadLogo={(duplaId, formData) =>
        handleAction(() => api.uploadLogo(duplaId, formData))
      }
      onFetchScores={(numero) =>
        handleAction(() => api.fetchScoresForRound(numero, desafioId))
      }
      onUpdateAllTimes={() => handleAction(() => api.updateAllTimes(desafioId))}
      onResetScores={() =>
        showNotification({
          title: "Zona de Perigo",
          message:
            "TEM A CERTEZA?\nIsto irá apagar TODAS as pontuações e rodadas DESTE desafio.",
          type: "confirm",
          onConfirm: () =>
            handleAction(
              () => api.resetarPontuacoes(desafioId),
              "Pontuações e rodadas do desafio foram resetadas."
            ),
        })
      }
      onResetScoresForRound={(numero) =>
        showNotification({
          title: "Confirmar Exclusão de Rodada",
          message: `Tem a certeza que quer apagar TODAS as pontuações da rodada ${numero} DESTE desafio?`,
          type: "confirm",
          onConfirm: () =>
            handleAction(
              () => api.resetScoresForRound(numero, desafioId),
              `Pontuações da rodada ${numero} apagadas com sucesso.`
            ),
        })
      }
    />
  );
}

function NotFound() {
  return (
    <div className="text-center text-white">
      <h2 className="text-2xl font-bold">404 - Página Não Encontrada</h2>
      <p className="text-gray-400">
        O link que seguiu pode estar quebrado ou a página pode ter sido
        removida.
      </p>
      <Link to="/" className="text-teal-400 hover:underline mt-4 inline-block">
        Voltar à Página Inicial
      </Link>
    </div>
  );
}
